import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const UPI_REGEX = /^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z][a-zA-Z0-9.\-_]{2,}$/;

const ArtistUpiForm = ({
    user,
    onComplete,
    onBack,
    heading = "Add Your UPI ID",
    subheading = "Tips from listeners will be sent directly to this UPI handle.",
}) => {
    const [upi, setUpi] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            if (!user?.uid) return;
            try {
                const userRef = doc(db, "users", user.uid);
                const snap = await getDoc(userRef);
                if (snap.exists() && active) {
                    const data = snap.data();
                    const existingUpi = data?.upi || data?.upiId || data?.upiHandle || "";
                    if (existingUpi) {
                        setUpi(existingUpi);
                    }
                }
            } catch (err) {
                console.error("Failed to prefill UPI:", err);
            }
        })();
        return () => {
            active = false;
        };
    }, [user?.uid]);

    const validateUpi = (value) => {
        if (!value.trim()) {
            return "UPI ID is required";
        }
        const normalized = value.trim().toLowerCase();
        if (!UPI_REGEX.test(normalized)) {
            return "Enter a valid UPI ID (example: name@bank)";
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!user?.uid) {
            setError("You must be signed in to continue.");
            return;
        }

        const validationError = validateUpi(upi);
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const normalized = upi.trim().toLowerCase();
            const userRef = doc(db, "users", user.uid);
            await setDoc(
                userRef,
                {
                    upi: normalized,
                    upiId: normalized,
                    upiHandle: normalized,
                    upiUpdatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            onComplete?.(normalized);
        } catch (err) {
            console.error("Failed to save UPI:", err);
            setError(err?.message || "Failed to save UPI. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold mb-2">{heading}</h1>
                    <p className="text-white/70">{subheading}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-200 text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-2">UPI ID</label>
                        <input
                            type="text"
                            value={upi}
                            onChange={(e) => setUpi(e.target.value)}
                            placeholder="example@bank"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                            autoComplete="off"
                            autoCapitalize="none"
                        />
                    </div>

                    <div className="flex gap-3">
                        {onBack && (
                            <button
                                type="button"
                                onClick={onBack}
                                className="flex-1 bg-white/10 text-white font-semibold py-3 rounded-lg hover:bg-white/20 transition"
                                disabled={loading}
                            >
                                Back
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] text-white font-semibold py-3 rounded-lg shadow-[0_8px_30px_rgba(255,0,0,0.35)] ring-1 ring-red-500/30 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Saving..." : "Save UPI"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ArtistUpiForm;

